import React, { useEffect, useState, useContext } from 'react';
import { ShippingBanner } from '../components/ShippingBanner';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/Button';
import { NavigationContext, CartContext, CartItem } from '../App';
import { CheckCircle2, Mail, Instagram, Package } from 'lucide-react';
export function OrderConfirmationPage() {
  const {
    navigate
  } = useContext(NavigationContext);
  const {
    cart,
    clearCart,
    cartTotal
  } = useContext(CartContext);
  const [orderItems, setOrderItems] = useState<CartItem[]>([]);
  const [orderTotal, setOrderTotal] = useState(0);
  useEffect(() => {
    if (cart.length > 0) {
      setOrderItems([...cart]);
      setOrderTotal(cartTotal);
      clearCart();
    }
  }, []);
  const subtotal = orderTotal || orderItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const shipping = subtotal >= 75 ? 0 : 8.95;
  const tax = subtotal * 0.0825;
  const total = subtotal + shipping + tax;
  const orderNumber = `CFC-${Math.floor(10000 + Math.random() * 90000)}`;
  if (orderItems.length === 0 && cart.length === 0) {
    return <div className="min-h-screen flex flex-col bg-[#FAFAFA]" data-id="element-904">
        <ShippingBanner data-id="element-905" />
        <Navbar data-id="element-906" />
        <main className="flex-grow py-12 lg:py-16" data-id="element-907">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 text-center" data-id="element-908">
            <h1 className="text-2xl sm:text-3xl font-['Playfair_Display'] text-[#1A1A1A] mb-4" data-id="element-909">
              No Order Found
            </h1>
            <p className="text-sm sm:text-base text-[#6B6B6B] font-['Raleway'] mb-8" data-id="element-910">
              It looks like you haven't placed an order yet.
            </p>
            <Button onClick={() => navigate('shop')} variant="outline" data-id="element-911">
              Start Shopping
            </Button>
          </div>
        </main>
        <Footer data-id="element-912" />
      </div>;
  }
  return <div className="min-h-screen flex flex-col bg-[#FAFAFA]" data-id="element-913">
      <ShippingBanner data-id="element-914" />
      <Navbar data-id="element-915" />

      <main className="flex-grow py-8 sm:py-12 lg:py-16" data-id="element-916">
        <div className="max-w-2xl mx-auto px-4 sm:px-6" data-id="element-917">
          {/* Success Header */}
          <div className="text-center mb-8 sm:mb-10" data-id="element-918">
            <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-[#D4A853]/10 mb-4 sm:mb-6" data-id="element-919">
              <CheckCircle2 size={40} className="text-[#D4A853] sm:w-12 sm:h-12" strokeWidth={1.5} data-id="element-920" />
            </div>
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-['Playfair_Display'] text-[#1A1A1A] mb-2 sm:mb-3" data-id="element-921">
              Thank You for Your Order!
            </h1>
            <p className="text-sm sm:text-base text-[#6B6B6B] font-['Raleway'] mb-2" data-id="element-922">
              Your order has been received and is awaiting payment.
            </p>
            <p className="text-xs sm:text-sm font-medium text-[#1A1A1A] font-['Raleway']" data-id="element-923">
              Order <span className="text-[#D4A853]" data-id="element-924">#{orderNumber}</span>
            </p>
          </div>

          {/* Order Summary */}
          <div className="bg-white border border-[#E8E8E8] p-4 sm:p-5 lg:p-6 mb-4 sm:mb-6" data-id="element-925">
            <h2 className="text-base sm:text-lg font-['Playfair_Display'] text-[#1A1A1A] mb-4 sm:mb-5" data-id="element-926">
              Order Summary
            </h2>
            <div className="space-y-3 sm:space-y-4 mb-4 sm:mb-5 pb-4 sm:pb-5 border-b border-[#E8E8E8]" data-id="element-927">
              {orderItems.map(item => <div key={`${item.id}-${item.size}-${item.color}`} className="flex gap-3" data-id="element-928">
                  <div className={`w-12 sm:w-14 h-[60px] sm:h-[70px] ${item.image} flex-shrink-0 bg-[#F5F5F5]`} data-id="element-929" />
                  <div className="flex-1 min-w-0" data-id="element-930">
                    <h3 className="text-xs sm:text-sm font-medium text-[#1A1A1A] font-['Playfair_Display'] leading-snug" data-id="element-931">
                      {item.name}
                    </h3>
                    <p className="text-[10px] sm:text-[11px] text-[#6B6B6B] mt-0.5 font-['Raleway']" data-id="element-932">
                      {item.size} · {item.color} · Qty {item.quantity}
                    </p>
                  </div>
                  <p className="text-xs sm:text-sm font-medium text-[#1A1A1A] font-['Raleway']" data-id="element-933">
                    ${(item.price * item.quantity).toFixed(2)}
                  </p>
                </div>)}
            </div>
            <div className="space-y-2 text-xs sm:text-sm font-['Raleway']" data-id="element-934">
              <div className="flex justify-between" data-id="element-935">
                <span className="text-[#6B6B6B]" data-id="element-936">Subtotal</span>
                <span className="text-[#1A1A1A]" data-id="element-937">${subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between" data-id="element-938">
                <span className="text-[#6B6B6B]" data-id="element-939">Shipping</span>
                <span className={shipping === 0 ? 'text-green-600 font-medium' : 'text-[#1A1A1A]'} data-id="element-940">
                  {shipping === 0 ? 'Free' : `$${shipping.toFixed(2)}`}
                </span>
              </div>
              <div className="flex justify-between" data-id="element-941">
                <span className="text-[#6B6B6B]" data-id="element-942">Tax</span>
                <span className="text-[#1A1A1A]" data-id="element-943">${tax.toFixed(2)}</span>
              </div>
              <div className="flex justify-between pt-3 mt-1 border-t border-[#E8E8E8]" data-id="element-944">
                <span className="font-semibold text-[#1A1A1A]" data-id="element-945">Total</span>
                <span className="text-base sm:text-lg font-bold text-[#D4A853]" data-id="element-946">
                  ${total.toFixed(2)}
                </span>
              </div>
            </div>
          </div>

          {/* Payment Instructions */}
          <div className="bg-[#D4A853]/10 border-2 border-[#D4A853]/30 p-4 sm:p-5 lg:p-6 mb-4 sm:mb-6" data-id="element-947">
            <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4" data-id="element-948">
              <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-[#D4A853]/20 flex items-center justify-center flex-shrink-0" data-id="element-949">
                <Package size={14} className="text-[#D4A853] sm:w-4 sm:h-4" strokeWidth={1.5} data-id="element-950" />
              </div>
              <h2 className="text-base sm:text-lg font-['Playfair_Display'] text-[#1A1A1A]" data-id="element-951">
                Next Step: Complete Your Payment
              </h2>
            </div>
            <p className="text-xs sm:text-sm text-[#1A1A1A]/80 font-['Raleway'] mb-4 sm:mb-5 leading-relaxed" data-id="element-952">
              Please send your payment of{' '}
              <span className="font-semibold text-[#D4A853]" data-id="element-953">
                ${total.toFixed(2)}
              </span>{' '}
              via your selected method:
            </p>
            <div className="space-y-3 sm:space-y-4 mb-4 sm:mb-5" data-id="element-954">
              <div className="bg-white/60 p-3 sm:p-4 rounded" data-id="element-955">
                <div className="flex items-center gap-2 mb-1 sm:mb-2" data-id="element-956">
                  <div className="w-5 h-5 sm:w-6 sm:h-6 bg-[#6D1ED4] rounded flex items-center justify-center" data-id="element-957">
                    <span className="text-white text-[8px] sm:text-[10px] font-bold" data-id="element-958">
                      Z
                    </span>
                  </div>
                  <span className="text-xs sm:text-sm font-semibold text-[#1A1A1A]" data-id="element-959">
                    Zelle
                  </span>
                </div>
                <p className="text-xs sm:text-sm text-[#6B6B6B] font-['Raleway']" data-id="element-960">
                  Send to:{' '}
                  <span className="font-medium text-[#1A1A1A]" data-id="element-961">
                    (555) 123-4567
                  </span>
                </p>
              </div>
              <div className="bg-white/60 p-3 sm:p-4 rounded" data-id="element-962">
                <div className="flex items-center gap-2 mb-1 sm:mb-2" data-id="element-963">
                  <div className="w-5 h-5 sm:w-6 sm:h-6 bg-[#00D632] rounded flex items-center justify-center" data-id="element-964">
                    <span className="text-white text-[8px] sm:text-[10px] font-bold" data-id="element-965">
                      $
                    </span>
                  </div>
                  <span className="text-xs sm:text-sm font-semibold text-[#1A1A1A]" data-id="element-966">
                    Cash App
                  </span>
                </div>
                <p className="text-xs sm:text-sm text-[#6B6B6B] font-['Raleway']" data-id="element-967">
                  Send to:{' '}
                  <span className="font-medium text-[#1A1A1A]" data-id="element-968">
                    $ChristianaBoutique
                  </span>
                </p>
              </div>
            </div>
            <p className="text-[10px] sm:text-xs text-[#6B6B6B] font-['Raleway'] leading-relaxed" data-id="element-969">
              Once payment is received, your order will be shipped within{' '}
              <span className="font-medium text-[#1A1A1A]" data-id="element-970">
                2–3 business days
              </span>
              . You'll receive a confirmation email with tracking information.
            </p>
          </div>

          {/* Contact Note */}
          <div className="bg-white border border-[#E8E8E8] p-4 sm:p-5 mb-6 sm:mb-8" data-id="element-971">
            <p className="text-xs sm:text-sm text-[#6B6B6B] font-['Raleway'] text-center leading-relaxed" data-id="element-972">
              <span className="font-medium text-[#1A1A1A]" data-id="element-973">Questions?</span>{' '}
              Reach out to us anytime.
            </p>
            <div className="flex flex-col items-center gap-3 sm:flex-row sm:justify-center sm:gap-4 mt-3 sm:mt-4" data-id="element-974">
              <a href="mailto:christiana.nnanna@email.com" className="inline-flex items-center gap-2 text-xs sm:text-sm text-[#6B6B6B] hover:text-[#D4A853] transition-colors" data-id="element-975">
                <Mail size={14} strokeWidth={1.5} data-id="element-976" />
                <span className="break-all" data-id="element-977">christiana.nnanna@email.com</span>
              </a>
              <span className="hidden sm:inline text-[#E8E8E8]" data-id="element-978">|</span>
              <a href="https://instagram.com/christiana.nnanna" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-xs sm:text-sm text-[#6B6B6B] hover:text-[#D4A853] transition-colors" data-id="element-979">
                <Instagram size={14} strokeWidth={1.5} data-id="element-980" />
                @christiana.nnanna
              </a>
            </div>
          </div>

          <div className="text-center" data-id="element-981">
            <Button onClick={() => navigate('shop')} variant="outline" className="min-w-[180px] sm:min-w-[200px]" data-id="element-982">
              Continue Shopping
            </Button>
          </div>
        </div>
      </main>

      <Footer data-id="element-983" />
    </div>;
}